import type { TaxRegime } from './fiscalTypes';

/** Códigos AFIP WSFEv1 (CbteTipo) — copia mínima para mobile. */
export const AFIP_CBTE = {
  FACTURA_A: 1,
  NOTA_DEBITO_A: 2,
  NOTA_CREDITO_A: 3,
  FACTURA_B: 6,
  NOTA_DEBITO_B: 7,
  NOTA_CREDITO_B: 8,
  FACTURA_C: 11,
  NOTA_DEBITO_C: 12,
  NOTA_CREDITO_C: 13,
} as const;

export const AFIP_IVA_RECEPTOR = {
  RESPONSABLE_INSCRIPTO: 1,
  EXENTO: 4,
  CONSUMIDOR_FINAL: 5,
  MONOTRIBUTO: 6,
  NO_CATEGORIZADO: 7,
  MONOTRIBUTO_SOCIAL: 13,
  MONOTRIBUTO_TIP: 16,
} as const;

/** Condiciones Cmp_Clase A/M/C — no válidas en Factura B (AFIP 10243). */
export function condicionIvaReceptorRequiresComprobanteClaseA(condicion: number): boolean {
  return (
    condicion === AFIP_IVA_RECEPTOR.RESPONSABLE_INSCRIPTO ||
    condicion === AFIP_IVA_RECEPTOR.MONOTRIBUTO ||
    condicion === AFIP_IVA_RECEPTOR.MONOTRIBUTO_SOCIAL ||
    condicion === AFIP_IVA_RECEPTOR.MONOTRIBUTO_TIP
  );
}

function upgradeCbteTipoToClaseA(cbteTipo: number): number {
  switch (cbteTipo) {
    case AFIP_CBTE.FACTURA_B:
      return AFIP_CBTE.FACTURA_A;
    case AFIP_CBTE.NOTA_CREDITO_B:
      return AFIP_CBTE.NOTA_CREDITO_A;
    case AFIP_CBTE.NOTA_DEBITO_B:
      return AFIP_CBTE.NOTA_DEBITO_A;
    default:
      return cbteTipo;
  }
}

/** Corrige pares inválidos Factura B + condición Cmp_Clase A/M/C (AFIP 10243). */
export function reconcileCbteTipoAndReceptorIva(
  cbteTipo: number,
  condicionIva: number,
): { cbteTipo: number; condicionIva: number } {
  if (condicionIvaReceptorRequiresComprobanteClaseA(condicionIva)) {
    const upgraded = upgradeCbteTipoToClaseA(cbteTipo);
    if (upgraded !== cbteTipo) {
      return { cbteTipo: upgraded, condicionIva };
    }
  }
  return { cbteTipo, condicionIva };
}

export type ResolveInvoiceTypeInput = {
  emitterRegime: TaxRegime;
  buyerTaxCondition?: number | null;
  isCreditNote?: boolean;
  isDebitNote?: boolean;
  originalInvoiceType?: number | null;
};

export function resolveInvoiceType(input: ResolveInvoiceTypeInput): number {
  const { emitterRegime, buyerTaxCondition, isCreditNote, originalInvoiceType } = input;

  if (isCreditNote && originalInvoiceType) {
    if (originalInvoiceType === AFIP_CBTE.FACTURA_A) return AFIP_CBTE.NOTA_CREDITO_A;
    if (originalInvoiceType === AFIP_CBTE.FACTURA_B) return AFIP_CBTE.NOTA_CREDITO_B;
    if (originalInvoiceType === AFIP_CBTE.FACTURA_C) return AFIP_CBTE.NOTA_CREDITO_C;
  }

  if (input.isDebitNote && originalInvoiceType) {
    if (originalInvoiceType === AFIP_CBTE.FACTURA_A) return AFIP_CBTE.NOTA_DEBITO_A;
    if (originalInvoiceType === AFIP_CBTE.FACTURA_B) return AFIP_CBTE.NOTA_DEBITO_B;
    if (originalInvoiceType === AFIP_CBTE.FACTURA_C) return AFIP_CBTE.NOTA_DEBITO_C;
  }

  if (emitterRegime === 'monotributo') {
    return AFIP_CBTE.FACTURA_C;
  }

  const cond = buyerTaxCondition ?? AFIP_IVA_RECEPTOR.CONSUMIDOR_FINAL;
  if (condicionIvaReceptorRequiresComprobanteClaseA(cond)) {
    return AFIP_CBTE.FACTURA_A;
  }
  return AFIP_CBTE.FACTURA_B;
}

export { monotributoLimitForCategory } from './fiscalPresentation';

/** Etiqueta AR de la condición IVA del receptor (códigos AFIP_IVA_RECEPTOR). */
export function spanishIvaReceptorLabel(cond?: number | null): string {
  switch (cond) {
    case AFIP_IVA_RECEPTOR.RESPONSABLE_INSCRIPTO:
      return 'Responsable Inscripto';
    case AFIP_IVA_RECEPTOR.EXENTO:
      return 'Exento';
    case AFIP_IVA_RECEPTOR.MONOTRIBUTO:
      return 'Monotributo';
    case AFIP_IVA_RECEPTOR.NO_CATEGORIZADO:
      return 'No categorizado';
    case AFIP_IVA_RECEPTOR.CONSUMIDOR_FINAL:
    default:
      return 'Consumidor Final';
  }
}

export function spanishInvoiceTypeLabel(cbteTipo?: number | null): string {
  if (cbteTipo == null) return 'Comprobante fiscal';
  switch (cbteTipo) {
    case AFIP_CBTE.FACTURA_A:
      return 'Factura A';
    case AFIP_CBTE.FACTURA_B:
      return 'Factura B';
    case AFIP_CBTE.FACTURA_C:
      return 'Factura C';
    case AFIP_CBTE.NOTA_CREDITO_A:
      return 'Nota de crédito A';
    case AFIP_CBTE.NOTA_CREDITO_B:
      return 'Nota de crédito B';
    case AFIP_CBTE.NOTA_CREDITO_C:
      return 'Nota de crédito C';
    case AFIP_CBTE.NOTA_DEBITO_A:
      return 'Nota de débito A';
    case AFIP_CBTE.NOTA_DEBITO_B:
      return 'Nota de débito B';
    case AFIP_CBTE.NOTA_DEBITO_C:
      return 'Nota de débito C';
    default:
      return `Comprobante ${cbteTipo}`;
  }
}
